import { FC, useState, ReactNode } from "react";
import cx from "classnames";
import Paper from "@mui/material/Paper";
import { MobilePaperTitle } from "@/components/PageTitle";
import {
  MenuIcon,
  GlobalDollarColoredIcon,
  AvatarIcon,
  GiftIcon,
  SupportIcon,
  AccountIcon,
  LogoShapeIcon,
  TripIcon,
  LogoutIcon,
  EditIcon,
} from "@/components/Icon";
import { IconButton } from "@/components/Button";
import Avatar from "@/components/Avatar";
import Typography from "@/components/Typography";
import Button from "@/components/Button";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import {
  useCurrentTrip,
  useLock,
  useDialog,
  useTrackers,
  useOpenExternalURL,
  useBrowserSettings,
  useFcmToken,
  useTripType,
  useManageNavigation,
} from "@/hooks";
import Divider from "@/components/Divider";
import compact from "lodash/compact";
import { useRouter } from "next/router";
import { useAuth0 } from "@auth0/auth0-react";
import { MenuItem } from "@/components/Menu";

import { TopLevelDialogNames } from "@/components/TopLevelDialogs";
import { useTasksQuery } from "@/fetch/progress";
import MobileTripSelector from "./MobileTripSelector";
import { useGlobalDollarBalanceQuery } from "@/fetch/rewards";
import { useRemoveFirebaseConnectionMutation } from "@/fetch/notifications";
import styles from "./MobileHeader.module.scss";

type Page = {
  name?: string;
  url?: string;
  onClick?: () => void;
  disabled?: boolean;
  renderItem?: () => ReactNode;
  icon?: ReactNode;
};

const MobileProfileMenu: FC = () => {
  const { isAllowedToNavigate } = useManageNavigation();
  const { isSuperliteTrip, isWHArrivalOnly } = useTripType();
  const { isGapp } = useBrowserSettings();
  const { isLoading: isLoadingAuth, logout: auth0Logout } = useAuth0();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { data: personalDetails, isLoading: isPersonalDetailsLoading } =
    usePersonalDetailsQuery();
  const { data: tasksData, isLoading: isTasksLoading } = useTasksQuery();
  const {
    data: globalDollarBalanceData,
    isLoading: isLoadingGlobalDollarBalance,
  } = useGlobalDollarBalanceQuery();
  const {
    mutateAsync: removeFirebaseConnections,
    isLoading: isRemoveFirebaseConnectionLoading,
  } = useRemoveFirebaseConnectionMutation();
  const { currentTrip } = useCurrentTrip();
  const hasNewTrip = false;
  const traveller = personalDetails?.traveller;
  const { push } = useRouter();
  const hasAssignedExpert = Boolean(tasksData?.staff?.length);
  const { openTopLevelDialog } = useDialog();
  const balance = globalDollarBalanceData?.result.balance || "0";
  const { fcmToken } = useFcmToken();

  const { track } = useTrackers();
  const openExternalURL = useOpenExternalURL();
  const websiteUrl = "https://globalworkandtravel.com";

  const handleClose = (page?: Page) => {
    setIsOpen(false);
    if (page?.onClick) {
      page.onClick();
    }

    if (page?.url) {
      push(page.url);
    }
  };

  const pages: Array<Page> = compact([
    hasAssignedExpert && !isSuperliteTrip && !isWHArrivalOnly
      ? {
          name: "Your Travel Experts",
          onClick: () => openTopLevelDialog(TopLevelDialogNames.Experts),
          disabled: !isAllowedToNavigate,
          icon: <AvatarIcon variant="outlined" />,
        }
      : null,
    {
      name: "Reward Centre",
      renderItem: () => (
        <div className={styles.globalDollarContainer}>
          <GlobalDollarColoredIcon className={styles.globalDollarIcon} />
          <Typography variant="body1" className={styles.balanceText}>
            {balance}
          </Typography>
        </div>
      ),
      url: "/rewards?tab=reward-center",
      disabled: !isAllowedToNavigate,
      icon: <GiftIcon />,
      onClick: () =>
        track("Reward Center Opened", { eventId: "reward-center-opened" }),
    },
    !isSuperliteTrip && !isWHArrivalOnly
      ? {
          name: "Help & Support",
          icon: <SupportIcon variant="outlined" />,
          onClick: () => {
            openTopLevelDialog(TopLevelDialogNames.Help);
            track("Help Opened", {
              eventId: "help-opened",
              trigger: "Profile menu",
            });
          },
        }
      : null,
    {
      name: "Account",
      url: "/account/general",
      icon: <AccountIcon />,
      disabled: !isAllowedToNavigate,
    },
    !isSuperliteTrip && !isWHArrivalOnly
      ? {
          name: "gWorld Welcome",
          icon: <LogoShapeIcon />,
          onClick: () => openTopLevelDialog(TopLevelDialogNames.OnboardingTour),
        }
      : null,
    {
      name: "Book a new trip",
      icon: <TripIcon />,
      onClick: () => {
        track("External Link Opened", {
          eventId: "external-link-opened",
          link: "website",
        });
        openExternalURL(websiteUrl);
      },
    },
  ]);

  const logout = async () => {
    try {
      if (isGapp && fcmToken?.deviceId) {
        await removeFirebaseConnections({ deviceId: fcmToken?.deviceId });
      }
    } catch (error) {
      console.error("FCM: Error with deleting deviceId", error);
    } finally {
      track("Sing Out", { eventId: "sign-out" });
      if (isGapp) {
        push("/logout");
      } else {
        auth0Logout({
          logoutParams: {
            returnTo: isGapp ? websiteUrl : global.window?.location.origin,
          },
        });
      }
    }
  };

  return (
    <>
      <IconButton
        onClick={() => setIsOpen(true)}
        className={styles.profileIconButton}
      >
        <MenuIcon />
      </IconButton>
      {isOpen && (
        <Paper elevation={0} className={styles.profileMenuPaper}>
          <MobilePaperTitle
            title="Profile & Settings"
            onGoBack={() => setIsOpen(false)}
          />
          <div className={styles.root}>
            <Avatar
              cloudinaryPath={traveller?.s3_profile_pic}
              size="xl"
              className={styles.avatar}
            />
            <Typography variant="h6" className={styles.name}>
              {`${traveller?.first_name || ""} ${
                traveller?.middle_name || ""
              } ${traveller?.last_name || ""}`}
            </Typography>
            <div className={styles.mobileButtonContainer}>
              <Button
                variant="outlined"
                endIcon={<EditIcon />}
                className={styles.mobileEditProfileButton}
                color="info"
                onClick={() =>
                  openTopLevelDialog(TopLevelDialogNames.EditProfile)
                }
              >
                Edit Profile
              </Button>
              <MobileTripSelector />
            </div>
            <Divider className={styles.divider} />
            {pages.map((eachPage) => (
              <MenuItem
                key={eachPage.name}
                className={styles.profileItemContainer}
                onClick={() => handleClose(eachPage)}
                disabled={eachPage.disabled}
              >
                {eachPage.icon}
                <Typography variant="body1" className={styles.itemTitle}>
                  {eachPage.name}
                </Typography>
                {eachPage.renderItem && eachPage.renderItem()}
              </MenuItem>
            ))}
            <Divider className={styles.divider} />
            <MenuItem className={styles.profileItemContainer} onClick={logout}>
              <LogoutIcon />
              <Typography variant="body1" className={styles.itemTitle}>
                {isRemoveFirebaseConnectionLoading
                  ? "Logging out..."
                  : "Log out"}
              </Typography>
            </MenuItem>
          </div>
        </Paper>
      )}
    </>
  );
};

export default MobileProfileMenu;
